/**
 * useAuditTrail Hook
 *
 * Manages audit trail entries with Firestore persistence:
 * - Real-time subscription to audit entries
 * - CRUD operations for audit entries
 * - Helper functions for creating different entry types
 *
 * Part of Phase 3: Submission Guidance & Review Tracking
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import type {
  AuditEntry,
  AuditEntryType,
  ConfidenceLevel,
  SystemDecisionEntry,
  UserActionEntry,
  UserOverrideEntry,
  DocumentEventEntry,
  StatusChangeEntry,
  ComplianceCheckEntry,
  SubmissionEventEntry,
  SubmissionMethod,
} from '@/types/jurisdiction';

interface UseAuditTrailReturn {
  entries: AuditEntry[];
  isLoading: boolean;
  error: string | null;

  // Add entries
  addSystemDecision: (params: {
    action: string;
    result: string;
    confidence: ConfidenceLevel;
    rules: string[];
    source?: string;
  }) => Promise<string>;

  addUserAction: (params: {
    description: string;
    field?: string;
    previousValue?: string;
    newValue?: string;
  }) => Promise<string>;

  addUserOverride: (params: {
    systemRecommendation: string;
    userDecision: string;
    reason: string;
    ruleCode?: string;
    resultingConfidence: ConfidenceLevel;
  }) => Promise<string>;

  addDocumentEvent: (params: {
    action: 'uploaded' | 'matched' | 'rejected' | 'deleted';
    fileName: string;
    fileType?: string;
    satisfiesRequirement?: string;
    autoMatched?: boolean;
  }) => Promise<string>;

  addStatusChange: (params: {
    entity: 'job' | 'permit' | 'inspection';
    previousStatus: string;
    newStatus: string;
    trigger?: string;
  }) => Promise<string>;

  addComplianceCheck: (params: {
    status: 'pass' | 'fail' | 'needs_review';
    score: number;
    rulesChecked: number;
    rulesPassed: number;
    criticalFindings: number;
    warningFindings: number;
    durationMs: number;
  }) => Promise<string>;

  addSubmissionEvent: (params: {
    method: SubmissionMethod;
    jurisdictionName: string;
    confirmationNumber?: string;
    submittedDocuments: string[];
    status: 'submitted' | 'confirmed' | 'failed';
  }) => Promise<string>;

  // Generic add for any entry type
  addEntry: (entry: Omit<AuditEntry, 'id' | 'jobId' | 'timestamp'>) => Promise<string>;

  // Update/delete
  updateEntry: (id: string, updates: Partial<AuditEntry>) => Promise<void>;
  deleteEntry: (id: string) => Promise<void>;

  // Bulk operations
  importEntries: (entries: Array<Omit<AuditEntry, 'id' | 'jobId' | 'timestamp'>>) => Promise<void>;
}

interface UseAuditTrailOptions {
  jobId: string;
}

export function useAuditTrail({ jobId }: UseAuditTrailOptions): UseAuditTrailReturn {
  const { user, profile, loading: authLoading } = useAuth();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get contractor ID - only use once profile is loaded to avoid permission issues
  const contractorId = profile?.contractorId || user?.uid;

  // Subscribe to audit_trail subcollection
  useEffect(() => {
    // Wait for auth to finish loading before attempting to access the collection
    if (authLoading) {
      return;
    }

    if (!contractorId || !jobId) {
      setEntries([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const auditRef = collection(db, `contractors/${contractorId}/jobs/${jobId}/audit_trail`);
    const q = query(auditRef, orderBy('timestamp', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const entriesData: AuditEntry[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          entriesData.push({
            id: doc.id,
            jobId,
            timestamp: data.timestamp?.toDate?.()?.toISOString() || data.timestamp,
            ...data,
          } as AuditEntry);
        });
        setEntries(entriesData);
        setIsLoading(false);
      },
      (err) => {
        console.error('Error fetching audit trail:', err);
        setError('Failed to load audit trail');
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [authLoading, contractorId, jobId]);

  // Helper to create base entry fields
  const createBaseEntry = useCallback(() => {
    return {
      timestamp: Timestamp.now(),
      actorId: user?.uid,
      actorName: profile?.fullName || profile?.email || user?.email || 'User',
    };
  }, [user, profile]);

  // Add a generic entry
  const addEntry = useCallback(
    async (entry: Omit<AuditEntry, 'id' | 'jobId' | 'timestamp'>): Promise<string> => {
      if (!contractorId || !user || !jobId) {
        throw new Error('Not authenticated or job not specified');
      }

      const auditRef = collection(db, `contractors/${contractorId}/jobs/${jobId}/audit_trail`);
      const docRef = await addDoc(auditRef, {
        ...entry,
        jobId,
        timestamp: Timestamp.now(),
      });

      return docRef.id;
    },
    [contractorId, user, jobId]
  );

  // Add system decision entry
  const addSystemDecision = useCallback(
    async (params: {
      action: string;
      result: string;
      confidence: ConfidenceLevel;
      rules: string[];
      source?: string;
    }): Promise<string> => {
      const entry: Omit<SystemDecisionEntry, 'id' | 'jobId' | 'timestamp'> = {
        type: 'system_decision',
        actorType: 'system',
        decision: {
          action: params.action,
          result: params.result,
          confidence: params.confidence,
          rules: params.rules,
          source: params.source,
        },
      };
      return addEntry(entry);
    },
    [addEntry]
  );

  // Add user action entry
  const addUserAction = useCallback(
    async (params: {
      description: string;
      field?: string;
      previousValue?: string;
      newValue?: string;
    }): Promise<string> => {
      const base = createBaseEntry();
      const entry: Omit<UserActionEntry, 'id' | 'jobId' | 'timestamp'> = {
        type: 'user_action',
        actorType: 'user',
        actorId: base.actorId,
        actorName: base.actorName,
        action: {
          description: params.description,
          field: params.field,
          previousValue: params.previousValue,
          newValue: params.newValue,
        },
      };
      return addEntry(entry);
    },
    [addEntry, createBaseEntry]
  );

  // Add user override entry
  const addUserOverride = useCallback(
    async (params: {
      systemRecommendation: string;
      userDecision: string;
      reason: string;
      ruleCode?: string;
      resultingConfidence: ConfidenceLevel;
    }): Promise<string> => {
      const base = createBaseEntry();
      const entry: Omit<UserOverrideEntry, 'id' | 'jobId' | 'timestamp'> = {
        type: 'user_override',
        actorType: 'user',
        actorId: base.actorId,
        actorName: base.actorName,
        override: {
          systemRecommendation: params.systemRecommendation,
          userDecision: params.userDecision,
          reason: params.reason,
          ruleCode: params.ruleCode,
          resultingConfidence: params.resultingConfidence,
        },
      };
      return addEntry(entry);
    },
    [addEntry, createBaseEntry]
  );

  // Add document event entry
  const addDocumentEvent = useCallback(
    async (params: {
      action: 'uploaded' | 'matched' | 'rejected' | 'deleted';
      fileName: string;
      fileType?: string;
      satisfiesRequirement?: string;
      autoMatched?: boolean;
    }): Promise<string> => {
      const base = createBaseEntry();
      const entry: Omit<DocumentEventEntry, 'id' | 'jobId' | 'timestamp'> = {
        type: 'document_event',
        actorType: params.autoMatched ? 'system' : 'user',
        actorId: params.autoMatched ? undefined : base.actorId,
        actorName: params.autoMatched ? undefined : base.actorName,
        document: {
          action: params.action,
          fileName: params.fileName,
          fileType: params.fileType,
          satisfiesRequirement: params.satisfiesRequirement,
          autoMatched: params.autoMatched,
        },
      };
      return addEntry(entry);
    },
    [addEntry, createBaseEntry]
  );

  // Add status change entry
  const addStatusChange = useCallback(
    async (params: {
      entity: 'job' | 'permit' | 'inspection';
      previousStatus: string;
      newStatus: string;
      trigger?: string;
    }): Promise<string> => {
      const base = createBaseEntry();
      const entry: Omit<StatusChangeEntry, 'id' | 'jobId' | 'timestamp'> = {
        type: 'status_change',
        actorType: 'user',
        actorId: base.actorId,
        actorName: base.actorName,
        statusChange: {
          entity: params.entity,
          previousStatus: params.previousStatus,
          newStatus: params.newStatus,
          trigger: params.trigger,
        },
      };
      return addEntry(entry);
    },
    [addEntry, createBaseEntry]
  );

  // Add compliance check entry
  const addComplianceCheck = useCallback(
    async (params: {
      status: 'pass' | 'fail' | 'needs_review';
      score: number;
      rulesChecked: number;
      rulesPassed: number;
      criticalFindings: number;
      warningFindings: number;
      durationMs: number;
    }): Promise<string> => {
      const entry: Omit<ComplianceCheckEntry, 'id' | 'jobId' | 'timestamp'> = {
        type: 'compliance_check',
        actorType: 'system',
        check: {
          status: params.status,
          score: params.score,
          rulesChecked: params.rulesChecked,
          rulesPassed: params.rulesPassed,
          criticalFindings: params.criticalFindings,
          warningFindings: params.warningFindings,
          durationMs: params.durationMs,
        },
      };
      return addEntry(entry);
    },
    [addEntry]
  );

  // Add submission event entry
  const addSubmissionEvent = useCallback(
    async (params: {
      method: SubmissionMethod;
      jurisdictionName: string;
      confirmationNumber?: string;
      submittedDocuments: string[];
      status: 'submitted' | 'confirmed' | 'failed';
    }): Promise<string> => {
      const base = createBaseEntry();
      const entry: Omit<SubmissionEventEntry, 'id' | 'jobId' | 'timestamp'> = {
        type: 'submission_event',
        actorType: 'user',
        actorId: base.actorId,
        actorName: base.actorName,
        submission: {
          method: params.method,
          jurisdictionName: params.jurisdictionName,
          confirmationNumber: params.confirmationNumber,
          submittedDocuments: params.submittedDocuments,
          status: params.status,
        },
      };
      return addEntry(entry);
    },
    [addEntry, createBaseEntry]
  );

  // Update an entry
  const updateEntry = useCallback(
    async (id: string, updates: Partial<AuditEntry>): Promise<void> => {
      if (!contractorId || !jobId) {
        throw new Error('Not authenticated or job not specified');
      }

      const entryRef = doc(db, `contractors/${contractorId}/jobs/${jobId}/audit_trail`, id);
      await updateDoc(entryRef, updates);
    },
    [contractorId, jobId]
  );

  // Delete an entry
  const deleteEntry = useCallback(
    async (id: string): Promise<void> => {
      if (!contractorId || !jobId) {
        throw new Error('Not authenticated or job not specified');
      }

      const entryRef = doc(db, `contractors/${contractorId}/jobs/${jobId}/audit_trail`, id);
      await deleteDoc(entryRef);
    },
    [contractorId, jobId]
  );

  // Import multiple entries at once
  const importEntries = useCallback(
    async (newEntries: Array<Omit<AuditEntry, 'id' | 'jobId' | 'timestamp'>>): Promise<void> => {
      if (!contractorId || !user || !jobId) {
        throw new Error('Not authenticated or job not specified');
      }

      const batch = writeBatch(db);
      const auditRef = collection(db, `contractors/${contractorId}/jobs/${jobId}/audit_trail`);
      const now = Timestamp.now();

      newEntries.forEach((entry) => {
        const docRef = doc(auditRef);
        batch.set(docRef, {
          ...entry,
          jobId,
          timestamp: now,
        });
      });

      await batch.commit();
    },
    [contractorId, user, jobId]
  );

  return {
    entries,
    isLoading,
    error,
    addSystemDecision,
    addUserAction,
    addUserOverride,
    addDocumentEvent,
    addStatusChange,
    addComplianceCheck,
    addSubmissionEvent,
    addEntry,
    updateEntry,
    deleteEntry,
    importEntries,
  };
}

export default useAuditTrail;
